import { useCallback, useState } from "react";

/**
 * Whether the Quote History view is on, and which carriers it reveals (ADR-031).
 *
 * Both are scoped to the SBS page on screen: the carrier picker's options and the board
 * filter are built from the page's coverage and requested plan, so a selection made on one
 * page names carriers that mean nothing on the next. They are held together here so that
 * leaving the view is a single call, made by whatever caused it — the toolbar button, or
 * the navigation that changes the page.
 *
 * This sits above the proposal fetch rather than inside `useQuoteHistory` because the view
 * picks the read scope (`all` vs `board`), which the fetch needs before it can resolve
 * which page is on screen.
 */
export const useQuoteHistoryView = (
  quoteHistoryEnabled: boolean,
): {
  effectiveShowHistory: boolean;
  selectedHistoryCarrierIds: string[];
  enterHistory: (carrierIds: string[]) => void;
  exitHistory: () => void;
} => {
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryCarrierIds, setSelectedHistoryCarrierIds] = useState<
    string[]
  >([]);

  // Entering always names carriers — the view shows nothing without them, which is also
  // why neither half of this is worth putting in the URL.
  const enterHistory = useCallback((carrierIds: string[]) => {
    setSelectedHistoryCarrierIds(carrierIds);
    setShowHistory(true);
  }, []);

  const exitHistory = useCallback(() => {
    setShowHistory(false);
    setSelectedHistoryCarrierIds([]);
  }, []);

  return {
    // Gate on the flag here so a disabled feature can't leave the board filtered.
    effectiveShowHistory: quoteHistoryEnabled && showHistory,
    selectedHistoryCarrierIds,
    enterHistory,
    exitHistory,
  };
};
